import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Legend, BarChart, Bar, Cell
} from 'recharts';
import {
  DollarSign, TrendingUp, TrendingDown, AlertCircle, ChevronDown,
  ChevronUp, Shield, Target, Activity, Clock, BarChart3, Layers,
  CheckCircle, XCircle, AlertTriangle, Info, Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// ─── Payoff Chart ───
const PayoffChart = ({ scenarios, breakevens, currentPrice }) => {
  const data = useMemo(() =>
    (scenarios || []).map(s => ({
      price: s.underlying_price,
      pricePct: `${s.price_change_pct > 0 ? '+' : ''}${s.price_change_pct}%`,
      pl: s.pl_dollar,
    })), [scenarios]);

  if (!data.length) return null;

  return (
    <Card className="border-0 shadow-lg" data-testid="payoff-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-indigo-600" />
          Expiration Payoff Diagram
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="price" tick={{ fontSize: 11 }} label={{ value: 'Underlying Price ($)', position: 'insideBottom', offset: -3, fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} label={{ value: 'P/L ($)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
            <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'P/L']}
              labelFormatter={(l) => `Price: $${l}`} />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
            {breakevens?.map((be, i) => (
              <ReferenceLine key={i} x={be} stroke="#f59e0b" strokeDasharray="4 4"
                label={{ value: `BE $${be}`, fill: '#d97706', fontSize: 10 }} />
            ))}
            {currentPrice && (
              <ReferenceLine x={currentPrice} stroke="#6366f1" strokeDasharray="4 4"
                label={{ value: `Now $${currentPrice}`, fill: '#6366f1', fontSize: 10 }} />
            )}
            <Line type="monotone" dataKey="pl" stroke="#6366f1" strokeWidth={2.5}
              dot={{ r: 4, fill: '#6366f1' }} activeDot={{ r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// ─── Quality Badge ───
const QualityBadge = ({ quality }) => {
  const map = {
    GOOD: { cls: 'bg-green-100 text-green-800', icon: CheckCircle },
    ACCEPTABLE: { cls: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    POOR: { cls: 'bg-orange-100 text-orange-800', icon: AlertCircle },
    INSUFFICIENT_DATA: { cls: 'bg-red-100 text-red-800', icon: XCircle },
  };
  const cfg = map[quality] || map.ACCEPTABLE;
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.cls} text-xs gap-1`}>
      <Icon className="h-3 w-3" />{quality?.replace('_', ' ')}
    </Badge>
  );
};

// ─── Metric Card ───
const MetricCard = ({ label, value, sub, positive, testId }) => (
  <div className={`rounded-lg p-3 text-center ${positive === true ? 'bg-green-50' : positive === false ? 'bg-red-50' : 'bg-slate-50'}`}
    data-testid={testId}>
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className={`text-xl font-bold ${positive === true ? 'text-green-700' : positive === false ? 'text-red-700' : 'text-gray-900'}`}>
      {value}
    </p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

// ─── Scenario Table ───
const ScenarioTable = ({ scenarios, title, label }) => {
  if (!scenarios?.length) return null;
  return (
    <div data-testid={`scenario-table-${label}`}>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[500px]">
          <thead>
            <tr className="bg-slate-100">
              {scenarios[0].underlying_price !== undefined && <th className="px-3 py-2 text-left">Price</th>}
              {scenarios[0].price_change_pct !== undefined && <th className="px-3 py-2 text-left">Change</th>}
              {scenarios[0].days_elapsed !== undefined && <th className="px-3 py-2 text-left">Days Elapsed</th>}
              {scenarios[0].days_remaining !== undefined && <th className="px-3 py-2 text-left">Days Left</th>}
              <th className="px-3 py-2 text-right">P/L</th>
              <th className="px-3 py-2 text-left">Type</th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((s, i) => (
              <tr key={i} className={`border-b border-slate-100 ${s.pl_dollar >= 0 ? '' : 'bg-red-50/30'}`}>
                {s.underlying_price !== undefined && <td className="px-3 py-1.5">${s.underlying_price}</td>}
                {s.price_change_pct !== undefined && <td className="px-3 py-1.5">{s.price_change_pct > 0 ? '+' : ''}{s.price_change_pct}%</td>}
                {s.days_elapsed !== undefined && <td className="px-3 py-1.5">{s.days_elapsed}</td>}
                {s.days_remaining !== undefined && <td className="px-3 py-1.5">{s.days_remaining}</td>}
                <td className={`px-3 py-1.5 text-right font-medium ${s.pl_dollar >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {s.pl_dollar >= 0 ? '+' : ''}${s.pl_dollar.toFixed(2)}
                </td>
                <td className="px-3 py-1.5">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${s.label === 'EXPIRATION_PAYOFF' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                    {s.label === 'EXPIRATION_PAYOFF' ? 'Exact' : 'Model Est.'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Greeks Display ───
const GreeksBar = ({ greeks, source }) => {
  if (!greeks) return null;
  const items = [
    { label: 'Delta', value: greeks.delta, color: 'text-blue-700' },
    { label: 'Gamma', value: greeks.gamma, color: 'text-purple-700' },
    { label: 'Theta', value: greeks.theta, color: greeks.theta < 0 ? 'text-red-700' : 'text-green-700' },
    { label: 'Vega', value: greeks.vega, color: 'text-teal-700' },
  ];
  return (
    <div className="flex flex-wrap gap-4 items-center" data-testid="greeks-bar">
      {items.map(g => (
        <div key={g.label} className="bg-slate-50 rounded-lg px-3 py-2 text-center min-w-[80px]">
          <p className="text-xs text-gray-500">{g.label}</p>
          <p className={`text-sm font-bold ${g.color}`}>
            {typeof g.value === 'number' ? g.value.toFixed(4) : 'N/A'}
          </p>
        </div>
      ))}
      <Badge variant="outline" className="text-xs">{source || 'MODEL_ESTIMATE'}</Badge>
    </div>
  );
};

// ─── Alternative Card ───
const AlternativeCard = ({ alt, isBaseline, currentPrice }) => {
  const [expanded, setExpanded] = useState(false);
  const m = alt.metrics || {};
  const score = alt.score || {};

  const maxLossStr = typeof m.max_loss === 'number' ? `$${Math.abs(m.max_loss).toFixed(0)}` : m.max_loss || 'N/A';
  const maxProfitStr = m.max_profit === 'UNLIMITED' ? 'Unlimited' :
    (typeof m.max_profit === 'number' ? `$${m.max_profit.toFixed(0)}` : 'N/A');

  return (
    <Card className={`border shadow-sm ${isBaseline ? 'border-indigo-300 bg-indigo-50/30' : 'border-slate-200'}`}
      data-testid={`alternative-card-${alt.type}`}>
      <div className="px-4 py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className={isBaseline ? 'bg-indigo-600' : 'bg-slate-600'}>
              {alt.type?.replace(/_/g, ' ')}
            </Badge>
            <span className="font-semibold text-gray-900">{alt.name}</span>
            <QualityBadge quality={alt.data_quality} />
          </div>
          <div className="flex items-center gap-3">
            {score.composite_score !== undefined && (
              <span className="text-xs text-gray-500">
                Score: <strong>{(score.composite_score * 100).toFixed(0)}</strong>/100
              </span>
            )}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>

        {/* Quick metrics row */}
        <div className="flex flex-wrap gap-4 mt-2 text-xs">
          <span>Max Loss: <strong className="text-red-700">{maxLossStr}</strong></span>
          <span>Max Profit: <strong className="text-green-700">{maxProfitStr}</strong></span>
          {m.breakevens?.length > 0 && (
            <span>Breakeven: <strong>{m.breakevens.map(b => `$${b}`).join(', ')}</strong></span>
          )}
          {m.greeks?.delta !== undefined && (
            <span>Delta: <strong>{m.greeks.delta.toFixed(2)}</strong></span>
          )}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 space-y-4 border-t border-slate-100">
          {/* Why considered */}
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200 mt-3">
            <p className="text-xs font-semibold text-amber-800 mb-1">Why This Alternative?</p>
            <p className="text-sm text-gray-700">{alt.why_considered}</p>
          </div>

          {/* Transactions */}
          {alt.transactions?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Transactions Required:</p>
              <div className="space-y-1">
                {alt.transactions.map((txn, i) => (
                  <div key={i} className="flex justify-between text-sm bg-slate-50 rounded px-3 py-1.5">
                    <span>{txn.action}: {txn.description}</span>
                    <span className="font-medium">
                      ${txn.estimated_price?.toFixed(2)}
                      {txn.price_type && <span className="text-xs text-gray-400 ml-1">({txn.price_type})</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Combined economics for rolls */}
          {alt.combined_economics && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-semibold text-blue-800 mb-2">Economic Breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {alt.combined_economics.total_realized_pl !== undefined && (
                  <div>
                    <span className="text-gray-500">Realized P/L:</span>
                    <span className={`ml-1 font-medium ${alt.combined_economics.total_realized_pl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      ${alt.combined_economics.total_realized_pl.toFixed(2)}
                    </span>
                  </div>
                )}
                {alt.combined_economics.net_additional_capital !== undefined && (
                  <div>
                    <span className="text-gray-500">Net {alt.combined_economics.net_additional_capital > 0 ? 'Debit' : 'Credit'}:</span>
                    <span className="ml-1 font-medium">${Math.abs(alt.combined_economics.net_additional_capital).toFixed(2)}</span>
                  </div>
                )}
                {alt.combined_economics.capital_released !== undefined && (
                  <div>
                    <span className="text-gray-500">Capital Released:</span>
                    <span className="ml-1 font-medium">${alt.combined_economics.capital_released.toFixed(2)}</span>
                  </div>
                )}
                {alt.combined_economics.credit_received !== undefined && (
                  <div>
                    <span className="text-gray-500">Credit Received:</span>
                    <span className="ml-1 font-medium text-green-700">${alt.combined_economics.credit_received.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Full metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <MetricCard label="Net Cost" value={`$${Math.abs(m.net_cost || 0).toFixed(0)}`} sub={m.net_cost >= 0 ? 'Credit' : 'Debit'} />
            <MetricCard label="Max Loss" value={maxLossStr} positive={false} />
            <MetricCard label="Max Profit" value={maxProfitStr} positive={true} />
            <MetricCard label="Breakeven" value={m.breakevens?.length ? `$${m.breakevens[0]}` : 'N/A'} />
          </div>

          {/* Greeks */}
          {m.greeks && <GreeksBar greeks={m.greeks} source={m.greeks_source} />}

          {/* Scenario P/L */}
          {m.scenarios?.length > 0 && (
            <ScenarioTable scenarios={m.scenarios} title="Expiration P/L at Various Prices" label={`alt-${alt.type}`} />
          )}

          {/* New position legs */}
          {alt.new_position_legs?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">New Position:</p>
              <div className="space-y-1">
                {alt.new_position_legs.map((nl, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-3 py-1.5">
                    <Badge variant="outline" className={nl.direction === 'long' ? 'text-green-700' : 'text-red-700'}>
                      {nl.direction.toUpperCase()}
                    </Badge>
                    <span>{nl.quantity}x ${nl.strike} {nl.option_type.toUpperCase()}</span>
                    <span className="text-gray-500">@ ${nl.entry_price?.toFixed(2)}</span>
                    {nl.bid && nl.ask && (
                      <span className="text-xs text-gray-400">
                        Bid: ${nl.bid.toFixed(2)} / Ask: ${nl.ask.toFixed(2)}
                      </span>
                    )}
                    {nl.volume !== null && (
                      <span className="text-xs text-gray-400">Vol: {nl.volume}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Score breakdown */}
          {score.individual_scores && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">
                Scoring ({score.profile})
              </p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(score.individual_scores).map(([k, v]) => (
                  <div key={k} className="text-xs bg-slate-50 rounded px-2 py-1">
                    {k.replace(/_/g, ' ')}: <strong>{(v * 100).toFixed(0)}%</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Data quality issues */}
          {alt.data_quality_issues?.length > 0 && (
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <p className="text-xs font-semibold text-red-800 mb-1">Data Quality Issues</p>
              <ul className="list-disc list-inside text-xs text-red-700 space-y-0.5">
                {alt.data_quality_issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ─── Comparison Table ───
const ComparisonTable = ({ alternatives }) => {
  if (!alternatives?.length || alternatives.length < 2) return null;

  return (
    <Card className="border-0 shadow-lg" data-testid="comparison-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Layers className="h-4 w-4 text-purple-600" />
          Side-by-Side Comparison
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[500px]">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-3 py-2 text-left">Strategy</th>
                <th className="px-3 py-2 text-right">Max Loss</th>
                <th className="px-3 py-2 text-right">Max Profit</th>
                <th className="px-3 py-2 text-right">Breakeven</th>
                <th className="px-3 py-2 text-right">Delta</th>
                <th className="px-3 py-2 text-right">Theta/day</th>
                <th className="px-3 py-2 text-center">Quality</th>
                <th className="px-3 py-2 text-right">Score</th>
              </tr>
            </thead>
            <tbody>
              {alternatives.map((alt, i) => {
                const m = alt.metrics || {};
                return (
                  <tr key={i} className={`border-b border-slate-100 ${alt.is_baseline ? 'bg-indigo-50/30' : ''}`}>
                    <td className="px-3 py-2 font-medium">{alt.name}</td>
                    <td className="px-3 py-2 text-right text-red-700">
                      {typeof m.max_loss === 'number' ? `$${Math.abs(m.max_loss).toFixed(0)}` : m.max_loss || 'N/A'}
                    </td>
                    <td className="px-3 py-2 text-right text-green-700">
                      {m.max_profit === 'UNLIMITED' ? 'Unlimited' :
                        (typeof m.max_profit === 'number' ? `$${m.max_profit.toFixed(0)}` : 'N/A')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {m.breakevens?.length ? `$${m.breakevens[0]}` : '-'}
                    </td>
                    <td className="px-3 py-2 text-right">{m.greeks?.delta?.toFixed(2) ?? '-'}</td>
                    <td className="px-3 py-2 text-right">{m.greeks?.theta?.toFixed(2) ?? '-'}</td>
                    <td className="px-3 py-2 text-center"><QualityBadge quality={alt.data_quality} /></td>
                    <td className="px-3 py-2 text-right font-bold">
                      {alt.score?.composite_score !== undefined ? (alt.score.composite_score * 100).toFixed(0) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── AI Analysis Section ───
const AIAnalysisSection = ({ aiAnalysis }) => {
  if (!aiAnalysis) return null;
  const hasContent = !!aiAnalysis.full_analysis;
  return (
    <Card className="border-0 shadow-lg" data-testid="ai-analysis-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4 text-indigo-600" />
          AI Analysis
          {aiAnalysis.model_used && (
            <Badge variant="outline" className="ml-2 text-xs">{aiAnalysis.model_used}</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasContent ? (
          <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap leading-relaxed"
            data-testid="ai-analysis-content">
            {aiAnalysis.full_analysis}
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2"
            data-testid="ai-analysis-error">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">AI explanation unavailable.</p>
              <p className="text-xs mt-0.5">
                {aiAnalysis.error || 'The AI model did not return an explanation.'} All calculated metrics above are valid — they are computed deterministically and do not depend on the AI.
              </p>
            </div>
          </div>
        )}
        {hasContent && aiAnalysis.error && (
          <div className="mt-3 bg-amber-50 p-2 rounded text-xs text-amber-700">
            <AlertCircle className="h-3 w-3 inline mr-1" />
            AI note: {aiAnalysis.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════
//  MAIN RESULTS COMPONENT
// ═══════════════════════════════════════════════════════

const PositionAnalysisResults = ({ analysis }) => {
  if (!analysis) return null;

  const pm = analysis.position_metrics || {};
  const cp = analysis.current_position || {};
  const snap = analysis.market_snapshot || {};
  const scenarios = analysis.scenarios || {};
  const alts = analysis.alternatives || [];
  const dq = analysis.data_quality || {};

  const legs = pm.legs_detail || [];
  const plPositive = pm.unrealized_pl >= 0;
  const degraded = dq.overall === 'INSUFFICIENT_DATA' || dq.overall === 'POOR';
  const SOURCE_LABEL = { MARKET_DATA: 'bid/ask mid', LAST_TRADE: 'last trade only', MODEL_ESTIMATE: 'model estimate', USER_PROVIDED: 'entered by you' };

  return (
    <div className="space-y-5 fade-in" data-testid="position-analysis-results">

      {degraded && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="data-quality-banner">
          <p className="font-semibold">Limited market data for this position ({dq.overall.replace('_', ' ')})</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            {(dq.issues || []).map((issue, i) => <li key={i}>{issue}</li>)}
          </ul>
          <p className="mt-2 text-xs">
            Current values below are priced from: {legs.map((l, i) => `Leg ${i + 1} — ${SOURCE_LABEL[l.current_price_source] || l.current_price_source}`).join('; ')}.
            No bid/ask quote means the figures are not executable prices.
          </p>
        </div>
      )}

      {/* ── Section 1: Current Position ── */}
      <Card className="border-0 shadow-lg" data-testid="current-position-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-indigo-600" />
              Your Current Position
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-base">
                {cp.underlying} @ ${cp.underlying_price}
              </Badge>
              <Badge variant="outline">
                <Clock className="h-3 w-3 mr-1" />{pm.days_to_expiration} DTE
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Key metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard label="Original Cost" value={`$${pm.original_cost?.toFixed(2)}`} testId="metric-original-cost" />
            <MetricCard label="Current Market Value" value={`$${pm.current_market_value?.toFixed(2)}`}
              sub={legs[0]?.current_price_source || ''} testId="metric-current-value" />
            <MetricCard label="Unrealized P/L" positive={plPositive}
              value={`${plPositive ? '+' : ''}$${pm.unrealized_pl?.toFixed(2)}`}
              sub={`${plPositive ? '+' : ''}${pm.unrealized_pl_pct?.toFixed(1)}%`}
              testId="metric-unrealized-pl" />
            <MetricCard label="Max Loss"
              value={typeof pm.max_loss === 'number' ? `$${Math.abs(pm.max_loss).toFixed(2)}` : pm.max_loss}
              positive={false} testId="metric-max-loss" />
            <MetricCard label="Breakeven"
              value={pm.breakevens_at_expiration?.length ? `$${pm.breakevens_at_expiration[0]}` : 'N/A'}
              sub="at expiration" testId="metric-breakeven" />
          </div>

          {/* Per-leg breakdown */}
          {legs.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2">Position Breakdown:</p>
              <div className="space-y-2">
                {legs.map((leg, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className={leg.direction === 'long' ? 'bg-green-600' : 'bg-red-600'}>
                          {leg.direction.toUpperCase()}
                        </Badge>
                        <span className="font-medium">{leg.quantity}x ${leg.strike} {leg.option_type.toUpperCase()}</span>
                        <Badge variant="outline" className="text-xs">{leg.moneyness}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span>Entry: <strong>${leg.entry_price}</strong></span>
                        <span>Current: <strong>${leg.current_mid?.toFixed(2)}</strong>
                          <span className="text-gray-400 ml-1">({leg.current_price_source})</span>
                        </span>
                        <span className={`font-bold ${leg.unrealized_pl >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {leg.unrealized_pl >= 0 ? '+' : ''}${leg.unrealized_pl?.toFixed(2)}
                          ({leg.unrealized_pl_pct >= 0 ? '+' : ''}{leg.unrealized_pl_pct?.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-gray-500">
                      <span>Intrinsic: ${leg.intrinsic_value?.toFixed(2)}</span>
                      <span>Extrinsic: ${leg.extrinsic_value?.toFixed(2)}</span>
                      {leg.implied_volatility && <span>IV: {(leg.implied_volatility * 100).toFixed(1)}%</span>}
                      {leg.current_bid != null && <span>Bid: ${leg.current_bid?.toFixed(2)}</span>}
                      {leg.current_ask != null && <span>Ask: ${leg.current_ask?.toFixed(2)}</span>}
                      {leg.current_bid != null && leg.current_ask != null && (
                        <span>Spread: ${(leg.current_ask - leg.current_bid).toFixed(2)}
                          ({leg.current_ask > 0 ? ((leg.current_ask - leg.current_bid) / leg.current_ask * 100).toFixed(1) : 0}%)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Combined Greeks */}
          <GreeksBar greeks={pm.combined_greeks} source={pm.combined_greeks?.source} />
        </CardContent>
      </Card>

      {/* ── Section 2: Payoff Chart ── */}
      <PayoffChart
        scenarios={scenarios.price_at_expiration}
        breakevens={pm.breakevens_at_expiration}
        currentPrice={cp.underlying_price}
      />

      {/* ── Section 3: Scenario Analysis ── */}
      <Card className="border-0 shadow-lg" data-testid="scenario-analysis-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calculator className="h-4 w-4 text-indigo-600" />
            Scenario Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ScenarioTable scenarios={scenarios.price_at_expiration}
            title="Price Scenarios at Expiration" label="expiration" />
          <ScenarioTable scenarios={scenarios.time_decay}
            title="Time Decay Analysis (at current price)" label="time" />
        </CardContent>
      </Card>

      {/* ── Section 4: Side-by-Side Comparison ── */}
      <ComparisonTable alternatives={alts} />

      {/* ── Section 5: Alternatives ── */}
      {alts.length > 0 && (
        <Card className="border-0 shadow-lg" data-testid="alternatives-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-600" />
              Potential Alternatives ({alts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {alts.map((alt, i) => (
              <AlternativeCard key={i} alt={alt} isBaseline={alt.is_baseline}
                currentPrice={cp.underlying_price} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* ── Section 6: AI Analysis ── */}
      <AIAnalysisSection aiAnalysis={analysis.ai_analysis} />

      {/* ── Section 7: Data Quality & Assumptions ── */}
      <Card className="border-0 shadow-sm border-t" data-testid="assumptions-card">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-600">Data Quality & Assumptions</span>
            </div>
            <div className="flex items-center gap-2">
              <QualityBadge quality={dq.overall} />
              <span className="text-xs text-gray-400">
                Snapshot: {snap.timestamp ? new Date(snap.timestamp).toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>
          {dq.issues?.length > 0 && (
            <div className="bg-amber-50 rounded p-2">
              <ul className="list-disc list-inside text-xs text-amber-700 space-y-0.5">
                {dq.issues.map((issue, i) => <li key={i}>{issue}</li>)}
              </ul>
            </div>
          )}
          <ul className="list-disc list-inside text-xs text-gray-500 space-y-0.5">
            {(analysis.assumptions || []).map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </CardContent>
      </Card>

      {/* ── Timestamp ── */}
      <p className="text-center text-xs text-gray-400">
        Analysis generated {new Date(analysis.timestamp).toLocaleString()} | {analysis.ai_model}
      </p>
    </div>
  );
};

export default PositionAnalysisResults;
